/**
 * Market Divergence Scanner — "Tìm cổ phiếu phân kì với thị trường"
 *
 * Two tabs:
 *   1. Confirmed  — stocks with strong, confirmed divergence (score ≥ 0.55)
 *   2. Watchlist   — early divergence signals to monitor before confirmation
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, CircularProgress, alpha, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Snackbar, Alert, Drawer,
  IconButton, TextField, InputAdornment, Skeleton, Select, MenuItem,
} from '@mui/material';
import {
  Refresh, Close, Search, Insights, TrendingUp, TrendingDown,
  ArrowUpward, ArrowDownward, Speed,
  FiberManualRecord, Bolt, Psychology, Shield, Visibility,
  FilterAlt, ShowChart, Equalizer, Public, Radar, TrackChanges,
  BarChart, SecurityOutlined, CategoryOutlined,
  SyncAlt, VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { marketDivergenceAPI, earlyDivergenceAPI } from '../services/api';

// ── Helpers ──────────────────────────────────────────────────────────
const fmtPct = (n, d = 2) => n != null ? `${n >= 0 ? '+' : ''}${(n * 100).toFixed(d)}%` : '—';
const fmtScore = (n) => n != null ? (n * 100).toFixed(1) : '—';

const SETUP_CONFIG = {
  leader_early:       { key: 'leader_early',       color: '#10b981', icon: <Bolt sx={{ fontSize: 14 }} /> },
  tight_ready:        { key: 'tight_ready',        color: '#6366f1', icon: <Speed sx={{ fontSize: 14 }} /> },
  quiet_accumulation: { key: 'quiet_accumulation',  color: '#f59e0b', icon: <Psychology sx={{ fontSize: 14 }} /> },
  watchlist:          { key: 'watchlist',           color: '#94a3b8', icon: <Shield sx={{ fontSize: 14 }} /> },
};

const EARLY_SETUP_CONFIG = {
  rs_turning:         { color: '#3b82f6', icon: <ShowChart sx={{ fontSize: 14 }} /> },
  accumulation_hint:  { color: '#f59e0b', icon: <Psychology sx={{ fontSize: 14 }} /> },
  support_holding:    { color: '#10b981', icon: <Speed sx={{ fontSize: 14 }} /> },
  momentum_building:  { color: '#8b5cf6', icon: <Bolt sx={{ fontSize: 14 }} /> },
  emerging:           { color: '#64748b', icon: <Radar sx={{ fontSize: 14 }} /> },
};

const PRIORITY_CFG = {
  'A+': { color: '#10b981', bg: '#ecfdf5', key: 'marketDivergence.priority.buy' },
  'A':  { color: '#3b82f6', bg: '#eff6ff', key: 'marketDivergence.priority.watch' },
  'B+': { color: '#f59e0b', bg: '#fffbeb', key: 'marketDivergence.priority.wait' },
  'B':  { color: '#94a3b8', bg: '#f8fafc', key: 'marketDivergence.priority.skip' },
};

// ── Skeletons ────────────────────────────────────────────────────────
const SummaryStripSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
    {/* Market Pulse skeleton */}
    <Box sx={{ minWidth: 260, borderRadius: 1.5, border: '1px solid #f1f5f9', bgcolor: '#fff', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 2, flexShrink: 0 }} />
      <Box sx={{ display: 'flex', gap: 2.5 }}>
        <Box>
          <Skeleton width={60} height={10} sx={{ mb: 0.5 }} />
          <Skeleton width={80} height={24} />
        </Box>
        <Box>
          <Skeleton width={24} height={10} sx={{ mb: 0.5 }} />
          <Skeleton width={80} height={24} />
        </Box>
      </Box>
    </Box>
    {/* Stat tile skeletons */}
    {[0, 1, 2, 3].map(i => (
      <Box key={i} sx={{ flex: '1 1 120px', minWidth: 110, borderRadius: 1.5, border: '1px solid #f1f5f9', bgcolor: '#fff', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
        <Box>
          <Skeleton width={50} height={10} sx={{ mb: 0.5 }} />
          <Skeleton width={40} height={22} />
        </Box>
      </Box>
    ))}
  </Box>
);

const SearchBarSkeleton = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Skeleton variant="rounded" width={180} height={32} sx={{ borderRadius: 2 }} />
      {[0, 1, 2].map(i => (
        <Skeleton key={i} variant="rounded" width={85} height={24} sx={{ borderRadius: 1.5 }} />
      ))}
    </Box>
    <Skeleton width={50} height={16} />
  </Box>
);

const TableSkeleton = () => (
  <Box sx={{ borderRadius: 2, border: '1px solid #f1f5f9', overflow: 'hidden' }}>
    {/* Header row */}
    <Box sx={{ display: 'flex', gap: 0, bgcolor: '#f8fafc', px: 2, py: 1.2 }}>
      {[24, 50, 60, 48, 72, 52, 52, 80, 200].map((w, i) => (
        <Box key={i} sx={{ width: w, flexShrink: 0, ...(i === 8 && { flex: 1 }) }}>
          <Skeleton width={w * 0.7} height={12} />
        </Box>
      ))}
    </Box>
    {/* Data rows */}
    {[...Array(10)].map((_, row) => (
      <Box key={row} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderTop: '1px solid #f8fafc' }}>
        <Box sx={{ width: 24, flexShrink: 0 }}><Skeleton width={16} height={14} /></Box>
        <Box sx={{ width: 50, flexShrink: 0 }}><Skeleton width={36} height={16} sx={{ fontWeight: 700 }} /></Box>
        <Box sx={{ width: 60, flexShrink: 0 }}><Skeleton width={50} height={14} /></Box>
        <Box sx={{ width: 48, flexShrink: 0 }}><Skeleton variant="rounded" width={38} height={20} sx={{ borderRadius: 1 }} /></Box>
        <Box sx={{ width: 72, flexShrink: 0 }}><Skeleton width={58} height={14} /></Box>
        <Box sx={{ width: 52, flexShrink: 0 }}><Skeleton width={52} height={14} /></Box>
        <Box sx={{ width: 52, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Skeleton variant="circular" width={8} height={8} />
          <Skeleton width={14} height={14} />
        </Box>
        <Box sx={{ width: 80, flexShrink: 0 }}><Skeleton variant="rounded" width={72} height={22} sx={{ borderRadius: 3 }} /></Box>
        <Box sx={{ flex: 1 }}><Skeleton width={`${60 + Math.random() * 30}%`} height={14} /></Box>
      </Box>
    ))}
  </Box>
);

// ── Empty state ──────────────────────────────────────────────────────
const EmptyState = ({ title, sub }) => (
  <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
    <Insights sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#475569' }}>{title}</Typography>
    {sub && <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>{sub}</Typography>}
  </Box>
);

// ═════════════════════════════════════════════════════════════════════
export default function MarketDivergence() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const T = useCallback((key, opts) => t(`marketDivergence.${key}`, opts), [t]);
  const TW = useCallback((key, opts) => t(`earlyDivergence.${key}`, opts), [t]);
  const fmtBillion = (n) => n != null ? `${(n / 1e9).toFixed(1)} ${T('detail.billion')}` : '—';
  const fmtBillionW = (n) => n != null ? `${(n / 1e9).toFixed(1)} ${TW('detail.billion')}` : '—';
  const setupLabel = (key) => T(`setup.${key}`);

  // ── Tab ─────────────────────────────────────────────────────────
  const [tab, setTab] = useState('confirmed');  // 'confirmed' | 'watchlist'

  // ── Confirmed state ────────────────────────────────────────────
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortCol, setSortCol] = useState('positive_div_score');
  const [sortDir, setSortDir] = useState('desc');
  const [detailTicker, setDetailTicker] = useState(null);
  const [sectorFilter, setSectorFilter] = useState('');
  const [blockedTickers, setBlockedTickers] = useState(() => {
    try { return JSON.parse(localStorage.getItem('div_blocked') || '[]'); } catch { return []; }
  });

  const unblockAll = () => { setBlockedTickers([]); localStorage.removeItem('div_blocked'); };

  // ── Watchlist state ────────────────────────────────────────────
  const [wlData, setWlData] = useState(null);
  const [wlLoading, setWlLoading] = useState(false);
  const [wlFirstLoad, setWlFirstLoad] = useState(true);
  const [wlSearch, setWlSearch] = useState('');
  const [wlSortCol, setWlSortCol] = useState('watchlist_rank');
  const [wlSortDir, setWlSortDir] = useState('desc');
  const [wlDetailTicker, setWlDetailTicker] = useState(null);
  const didInitialLoad = React.useRef(false);

  // ── Scan confirmed ─────────────────────────────────────────────
  const runScan = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await marketDivergenceAPI.scan({
        top_n: 50, min_score: 0.55, only_positive: true,
      });
      setData(res);
      setIsFirstLoad(false);
    } catch (e) {
      setError(e?.response?.data?.detail || T('error'));
    } finally {
      setLoading(false);
    }
  }, [T]);

  // ── Scan watchlist ─────────────────────────────────────────────
  const runWatchlist = useCallback(async () => {
    setWlLoading(true);
    setError('');
    try {
      const { data: res } = await earlyDivergenceAPI.scan({ top_n: 40 });
      setWlData(res);
      setWlFirstLoad(false);
    } catch (e) {
      setError(e?.response?.data?.detail || TW('error'));
    } finally {
      setWlLoading(false);
    }
  }, [TW]);

  // auto-scan on first mount
  React.useEffect(() => {
    if (!didInitialLoad.current) {
      didInitialLoad.current = true;
      runScan();
      runWatchlist();
    }
  }, [runScan, runWatchlist]);

  const isConfirmed = tab === 'confirmed';
  const curLoading = isConfirmed ? loading : wlLoading;

  const results = useMemo(() => data?.results || [], [data?.results]);
  const summary = data?.summary || {};
  const wlResults = useMemo(() => wlData?.results || [], [wlData?.results]);
  const wlSummary = wlData?.summary || {};

  // ── Active summary (for Market Pulse — shared) ────────────────
  const activeSummary = isConfirmed ? summary : wlSummary;

  const sectorName = (r) => (lang === 'vi' ? r.sector_name_vi : r.sector_name) || r.sector || '';

  // Unique sectors from both tabs
  const sectorList = useMemo(() => {
    const all = [...results, ...wlResults];
    const map = new Map();
    all.forEach(r => {
      const name = (lang === 'vi' ? r.sector_name_vi : r.sector_name) || r.sector;
      if (name && name !== 'UNKNOWN') map.set(r.sector, name);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [results, wlResults, lang]);

  // ── Filter & Sort — Confirmed ──────────────────────────────────
  const filtered = results
    .filter(r => !blockedTickers.includes(r.ticker))
    .filter(r => !sectorFilter || r.sector === sectorFilter)
    .filter(r => !searchTerm || r.ticker?.toLowerCase().includes(searchTerm.toLowerCase()) || r.company_name?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      const va = a[sortCol] ?? -Infinity;
      const vb = b[sortCol] ?? -Infinity;
      return sortDir === 'desc' ? vb - va : va - vb;
    });

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  // ── Filter & Sort — Watchlist ──────────────────────────────────
  const wlFiltered = wlResults
    .filter(r => !blockedTickers.includes(r.ticker))
    .filter(r => !sectorFilter || r.sector === sectorFilter)
    .filter(r => !wlSearch || r.ticker?.toLowerCase().includes(wlSearch.toLowerCase()) || r.company_name?.toLowerCase().includes(wlSearch.toLowerCase()))
    .sort((a, b) => {
      const va = a[wlSortCol] ?? -Infinity;
      const vb = b[wlSortCol] ?? -Infinity;
      return wlSortDir === 'desc' ? vb - va : va - vb;
    });

  const toggleWlSort = (col) => {
    if (wlSortCol === col) setWlSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setWlSortCol(col); setWlSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    const active = isConfirmed ? sortCol : wlSortCol;
    const dir = isConfirmed ? sortDir : wlSortDir;
    if (active !== col) return null;
    return dir === 'desc'
      ? <ArrowDownward sx={{ fontSize: 12, ml: 0.3 }} />
      : <ArrowUpward sx={{ fontSize: 12, ml: 0.3 }} />;
  };

  // ── Detail dialog ──────────────────────────────────────────────
  const detailRow = detailTicker ? results.find(r => r.ticker === detailTicker) : null;
  const wlDetailRow = wlDetailTicker ? wlResults.find(r => r.ticker === wlDetailTicker) : null;
  const getEarlyCfg = (key) => EARLY_SETUP_CONFIG[key] || EARLY_SETUP_CONFIG.emerging;

  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, md: 3 }, py: 2 }}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: isConfirmed
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isConfirmed
              ? '0 4px 14px rgba(16, 185, 129, 0.3)'
              : '0 4px 14px rgba(245, 158, 11, 0.3)',
            transition: 'all 0.3s',
          }}>
            {isConfirmed
              ? <Insights sx={{ fontSize: 22, color: '#fff' }} />
              : <Visibility sx={{ fontSize: 22, color: '#fff' }} />
            }
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {isConfirmed ? T('title') : TW('title')}
              </Typography>
              {activeSummary?.scan_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isConfirmed ? '#10b981' : '#f59e0b', boxShadow: `0 0 6px ${isConfirmed ? '#10b981' : '#f59e0b'}` }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>{activeSummary.scan_date.split(' ')[0]}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {isConfirmed ? T('subtitle') : TW('subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={curLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
          disabled={curLoading}
          onClick={isConfirmed ? runScan : runWatchlist}
          sx={{
            textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
            bgcolor: isConfirmed ? '#10b981' : '#f59e0b', color: '#fff', px: 2,
            '&:hover': { bgcolor: isConfirmed ? '#059669' : '#d97706' },
            '&:disabled': { bgcolor: '#94a3b8', color: '#fff' },
            transition: 'all 0.3s',
          }}
        >
          {curLoading ? T('scanning') : T('rescan')}
        </Button>
      </Box>

      {/* ── Tab switcher ────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 2, p: 0.5, borderRadius: 2, bgcolor: '#f1f5f9', width: 'fit-content' }}>
        {[
          { key: 'confirmed', label: T('tabs.confirmed'), Icon: Insights, count: results.length, accent: '#10b981' },
          { key: 'watchlist', label: T('tabs.watchlist'), Icon: Visibility, count: wlResults.length, accent: '#f59e0b' },
        ].map(({ key, label, Icon, count, accent }) => {
          const active = tab === key;
          return (
            <Box
              key={key}
              onClick={() => setTab(key)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.7,
                px: 2, py: 0.8, borderRadius: 1.5, cursor: 'pointer',
                bgcolor: active ? '#fff' : 'transparent',
                boxShadow: active ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
                transition: 'all 0.2s',
                '&:hover': !active ? { bgcolor: alpha('#fff', 0.5) } : {},
              }}
            >
              <Icon sx={{ fontSize: 16, color: active ? accent : '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: active ? 700 : 500, color: active ? '#1e293b' : '#64748b' }}>
                {label}
              </Typography>
              {count > 0 && (
                <Box sx={{
                  px: 0.7, py: 0.1, borderRadius: 1,
                  bgcolor: active ? alpha(accent, 0.1) : '#e2e8f0',
                  fontSize: '0.65rem', fontWeight: 800,
                  color: active ? accent : '#94a3b8',
                  fontFamily: 'monospace', lineHeight: 1.4,
                }}>
                  {count}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ════════════════════════════════════════════════════════════
           CONFIRMED TAB
         ════════════════════════════════════════════════════════════ */}
      {isConfirmed && (
        <>
          {/* Market Pulse + Stats */}
          {isFirstLoad ? (
            <SummaryStripSkeleton />
          ) : data && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              <MarketPulseCard summary={summary} />
              {[
                { label: T('tiles.universe'), val: summary.total_universe, color: '#64748b', Icon: Public },
                { label: T('tiles.positive'), val: summary.positive_count, color: '#10b981', Icon: TrendingUp },
                { label: T('tiles.avgScore'), val: fmtScore(summary.avg_score), color: '#6366f1', Icon: Equalizer },
                ...(summary.filtered_by_safety > 0 ? [{ label: T('tiles.filteredSafety'), val: summary.filtered_by_safety, color: '#f97316', Icon: FilterAlt }] : []),
              ].map((s, i) => <StatTile key={i} {...s} />)}
            </Box>
          )}

          {/* Market Context — regime, breadth, sectors */}
          {data && <MarketContextStrip summary={summary} />}

          {/* Search + Setup pills */}
          {data && results.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SearchField value={searchTerm} onChange={setSearchTerm} placeholder={T('searchPlaceholder')} accent="#10b981" />
                  <Select size="small" value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)} displayEmpty
                    sx={{ minWidth: 160, height: 32, fontSize: '0.78rem', fontWeight: 600, borderRadius: 1.5,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                      '& .MuiSelect-select': { py: 0.5 } }}>
                    <MenuItem value="" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{T('table.allSectors')}</MenuItem>
                    {sectorList.map(([code, name]) => (
                      <MenuItem key={code} value={code} sx={{ fontSize: '0.78rem' }}>{name}</MenuItem>
                    ))}
                  </Select>
                  {blockedTickers.length > 0 && (
                    <Tooltip title={T('table.hidingTickers', { count: blockedTickers.length })} arrow>
                      <Chip icon={<VisibilityOff sx={{ fontSize: 14 }} />} label={blockedTickers.length} size="small" onClick={unblockAll}
                        sx={{ height: 24, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', cursor: 'pointer' }} />
                    </Tooltip>
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', flexShrink: 0 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>{filtered.length}</Box> / {results.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                {Object.entries(summary.setup_distribution || {}).map(([key, count]) => {
                  const cfg = SETUP_CONFIG[key] || SETUP_CONFIG.watchlist;
                  return (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.3, borderRadius: 1.5, bgcolor: alpha(cfg.color, 0.06), border: `1px solid ${alpha(cfg.color, 0.1)}` }}>
                      <Box sx={{ color: cfg.color, display: 'flex', fontSize: 12 }}>{cfg.icon}</Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: cfg.color }}>{setupLabel(cfg.key)}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: cfg.color, fontFamily: 'monospace' }}>{count}</Typography>
                    </Box>
                  );
                })}
                {Object.keys(summary.priority_distribution || {}).length > 0 && (
                  <Box sx={{ width: '1px', height: 18, bgcolor: '#e2e8f0', mx: 0.3 }} />
                )}
                {Object.entries(summary.priority_distribution || {}).map(([key, count]) => {
                  const p = PRIORITY_CFG[key] || PRIORITY_CFG['B'];
                  return (
                    <Box key={`p-${key}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.7, py: 0.3, borderRadius: 1.5, bgcolor: p.bg, border: `1px solid ${alpha(p.color, 0.15)}` }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: p.color }}>{t(p.key)}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: p.color, fontFamily: 'monospace' }}>{count}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Table */}
          {isFirstLoad ? (
            <><SearchBarSkeleton /><TableSkeleton /></>
          ) : filtered.length === 0 ? (
            <EmptyState
              title={data ? T('table.noResults') : T('table.startScan')}
              sub={data && summary.market_weak === false ? T('table.marketNotWeak') : undefined}
            />
          ) : (
            <TableContainer sx={{ borderRadius: 2, border: '1px solid #f1f5f9' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('table.ticker')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', minWidth: 180 }}>{T('table.company')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', minWidth: 120 }}>{T('table.sector')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('close')}>
                      {T('table.price')} <SortIcon col="close" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('positive_div_score')}>
                      {T('table.score')} <SortIcon col="positive_div_score" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('excess_ret_5d')}>
                      {T('table.excessRet5d')} <SortIcon col="excess_ret_5d" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('rs_ret_5d')}>
                      RS 5d <SortIcon col="rs_ret_5d" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('table.greenDays')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('table.setup')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('priority.header')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('table.reason')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((r, i) => {
                    const setup = SETUP_CONFIG[r.setup_label] || SETUP_CONFIG.watchlist;
                    return (
                      <TableRow key={r.ticker} hover onClick={() => setDetailTicker(r.ticker)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#10b981', 0.03) } }}>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                        <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>{r.ticker}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '0.7rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.company_name || '—'}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>{sectorName(r)}</Typography></TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace' }}>{r.close?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: 1, bgcolor: alpha('#10b981', 0.08), color: '#059669', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace' }}>
                            {fmtScore(r.positive_div_score)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace', color: (r.excess_ret_5d || 0) > 0 ? '#10b981' : '#ef4444' }}>
                          {fmtPct(r.excess_ret_5d)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace', color: (r.rs_ret_5d || 0) > 0 ? '#10b981' : '#ef4444' }}>
                          {fmtPct(r.rs_ret_5d)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={T('table.greenDaysTooltip')} arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FiberManualRecord sx={{ fontSize: 8, color: '#10b981' }} />
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{r.green_when_index_red_10 ?? '—'}</Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip icon={setup.icon} label={setupLabel(setup.key)} size="small"
                            sx={{ fontSize: '0.68rem', fontWeight: 600, height: 22, bgcolor: alpha(setup.color, 0.08), color: setup.color, '& .MuiChip-icon': { color: setup.color } }} />
                        </TableCell>
                        <TableCell>
                          {(() => { const p = PRIORITY_CFG[r.priority] || PRIORITY_CFG['B']; return (
                            <Chip label={t(p.key)} size="small" sx={{ fontSize: '0.7rem', fontWeight: 700, height: 22, bgcolor: p.bg, color: p.color, border: `1px solid ${alpha(p.color, 0.3)}` }} />
                          ); })()}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.7rem', color: '#64748b', minWidth: 280, whiteSpace: 'normal', lineHeight: 1.5 }}>{r.reason}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════
           WATCHLIST TAB
         ════════════════════════════════════════════════════════════ */}
      {!isConfirmed && (
        <>
          {/* Watchlist Stats */}
          {wlFirstLoad ? (
            <SummaryStripSkeleton />
          ) : wlData && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              <MarketPulseCard summary={wlSummary} />
              {[
                { label: T('tiles.universe'), val: wlSummary.total_universe, color: '#64748b', Icon: Public },
                { label: TW('tiles.watchlist'), val: wlSummary.watchlist_count, color: '#f59e0b', Icon: Visibility },
                { label: TW('tiles.nearConfirm'), val: wlSummary.near_confirm_count ?? 0, color: '#10b981', Icon: TrackChanges },
                { label: TW('tiles.avgScore'), val: fmtScore(wlSummary.avg_score), color: '#6366f1', Icon: Radar },
              ].map((s, i) => <StatTile key={i} {...s} />)}
            </Box>
          )}

          {/* Market Context — regime, breadth, sectors */}
          {wlData && <MarketContextStrip summary={wlSummary} />}

          {/* Search + Setup pills */}
          {wlData && wlResults.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SearchField value={wlSearch} onChange={setWlSearch} placeholder={TW('searchPlaceholder')} accent="#f59e0b" />
                  <Select size="small" value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)} displayEmpty
                    sx={{ minWidth: 160, height: 32, fontSize: '0.78rem', fontWeight: 600, borderRadius: 1.5,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                      '& .MuiSelect-select': { py: 0.5 } }}>
                    <MenuItem value="" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{TW('table.allSectors')}</MenuItem>
                    {sectorList.map(([code, name]) => (
                      <MenuItem key={code} value={code} sx={{ fontSize: '0.78rem' }}>{name}</MenuItem>
                    ))}
                  </Select>
                  {blockedTickers.length > 0 && (
                    <Tooltip title={TW('table.hidingTickers', { count: blockedTickers.length })} arrow>
                      <Chip icon={<VisibilityOff sx={{ fontSize: 14 }} />} label={blockedTickers.length} size="small" onClick={unblockAll}
                        sx={{ height: 24, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', cursor: 'pointer' }} />
                    </Tooltip>
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', flexShrink: 0 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>{wlFiltered.length}</Box> / {wlResults.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                {Object.entries(wlSummary.setup_distribution || {}).map(([key, count]) => {
                  const cfg = getEarlyCfg(key);
                  return (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.3, borderRadius: 1.5, bgcolor: alpha(cfg.color, 0.06), border: `1px solid ${alpha(cfg.color, 0.1)}` }}>
                      <Box sx={{ color: cfg.color, display: 'flex', fontSize: 12 }}>{cfg.icon}</Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: cfg.color }}>{TW(`setup.${key}`)}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: cfg.color, fontFamily: 'monospace' }}>{count}</Typography>
                    </Box>
                  );
                })}
                {Object.keys(wlSummary.priority_distribution || {}).length > 0 && (
                  <Box sx={{ width: '1px', height: 18, bgcolor: '#e2e8f0', mx: 0.3 }} />
                )}
                {Object.entries(wlSummary.priority_distribution || {}).map(([key, count]) => {
                  const p = PRIORITY_CFG[key] || PRIORITY_CFG['B'];
                  return (
                    <Box key={`p-${key}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.7, py: 0.3, borderRadius: 1.5, bgcolor: p.bg, border: `1px solid ${alpha(p.color, 0.15)}` }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: p.color }}>{t(p.key)}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: p.color, fontFamily: 'monospace' }}>{count}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Watchlist Table */}
          {wlFirstLoad ? (
            <><SearchBarSkeleton /><TableSkeleton /></>
          ) : wlFiltered.length === 0 ? (
            <EmptyState
              title={wlData ? TW('table.noResults') : TW('table.startScan')}
              sub={wlData ? TW('table.noResultsSub') : undefined}
            />
          ) : (
            <TableContainer sx={{ borderRadius: 2, border: '1px solid #f1f5f9' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{TW('table.ticker')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', minWidth: 180 }}>{TW('table.company')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', minWidth: 120 }}>{TW('table.sector')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleWlSort('close')}>
                      {TW('table.price')} <SortIcon col="close" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleWlSort('positive_div_score')}>
                      {TW('table.score')} <SortIcon col="positive_div_score" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleWlSort('excess_ret_3d')}>
                      {TW('table.excessRet3d')} <SortIcon col="excess_ret_3d" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleWlSort('rs_slope_10')}>
                      {TW('table.rsSlope')} <SortIcon col="rs_slope_10" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{TW('table.greenDays')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleWlSort('early_signal_count')}>
                      {TW('table.signals')} <SortIcon col="early_signal_count" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{TW('table.setup')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{T('priority.header')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{TW('table.reason')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wlFiltered.map((r, i) => {
                    const setup = getEarlyCfg(r.early_setup);
                    return (
                      <TableRow key={r.ticker} hover onClick={() => setWlDetailTicker(r.ticker)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#f59e0b', 0.03) } }}>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                        <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>{r.ticker}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '0.7rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.company_name || '—'}</Typography></TableCell>
                        <TableCell><Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>{sectorName(r)}</Typography></TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace' }}>{r.close?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: 1, bgcolor: alpha('#f59e0b', 0.08), color: '#d97706', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace' }}>
                            {fmtScore(r.positive_div_score)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace', color: (r.excess_ret_3d || 0) > 0 ? '#10b981' : '#ef4444' }}>
                          {fmtPct(r.excess_ret_3d)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace', color: (r.rs_slope_10 || 0) > 0 ? '#10b981' : '#ef4444' }}>
                          {r.rs_slope_10 != null ? r.rs_slope_10.toFixed(5) : '—'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={TW('table.greenDaysTooltip')} arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FiberManualRecord sx={{ fontSize: 8, color: '#10b981' }} />
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{r.green_when_index_red_10 ?? '—'}</Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={TW('table.signalsTooltip')} arrow>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', bgcolor: alpha('#f59e0b', 0.1), color: '#d97706', fontSize: '0.72rem', fontWeight: 800 }}>
                              {r.early_signal_count ?? 0}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip icon={setup.icon} label={TW(`setup.${r.early_setup}`)} size="small"
                            sx={{ fontSize: '0.68rem', fontWeight: 600, height: 22, bgcolor: alpha(setup.color, 0.08), color: setup.color, '& .MuiChip-icon': { color: setup.color } }} />
                        </TableCell>
                        <TableCell>
                          {(() => { const p = PRIORITY_CFG[r.priority] || PRIORITY_CFG['B']; return (
                            <Chip label={t(p.key)} size="small" sx={{ fontSize: '0.7rem', fontWeight: 700, height: 22, bgcolor: p.bg, color: p.color, border: `1px solid ${alpha(p.color, 0.3)}` }} />
                          ); })()}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.7rem', color: '#64748b', minWidth: 280, whiteSpace: 'normal', lineHeight: 1.5 }}>{r.early_reason}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── Confirmed Detail Drawer ─────────────────────────────── */}
      <Drawer anchor="right" open={!!detailRow} onClose={() => setDetailTicker(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 0 } }}>
        {detailRow && (() => {
          const setup = SETUP_CONFIG[detailRow.setup_label] || SETUP_CONFIG.watchlist;
          const scoreVal = (detailRow.positive_div_score || 0) * 100;
          const rsiVal = detailRow.rsi14 || 50;
          return (
          <>
            {/* Header */}
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                {/* Ticker logo box */}
                <Box sx={{ width: 48, height: 48, borderRadius: 0.75, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ flex: 1, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.82rem', lineHeight: 1, letterSpacing: '0.02em' }}>{detailRow.ticker}</Typography>
                  </Box>
                  <Box sx={{ height: 13, bgcolor: setup.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '0.48rem', letterSpacing: '0.1em', lineHeight: 1, px: 0.3, textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 44 }}>{setupLabel(setup.key)}</Typography>
                  </Box>
                </Box>
                {/* Info area */}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>{detailRow.ticker}</Typography>
                    {(() => { const p = PRIORITY_CFG[detailRow.priority] || PRIORITY_CFG['B']; return (
                      <Chip label={t(p.key)} size="small" sx={{ fontWeight: 800, fontSize: '0.6rem', height: 18, bgcolor: p.bg, color: p.color, border: `1px solid ${alpha(p.color, 0.3)}` }} />
                    ); })()}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.35, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.64rem', color: '#94a3b8' }}>{T('detail.scoreLabel')}</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 900, fontFamily: 'monospace', lineHeight: 1,
                      color: scoreVal >= 60 ? '#10b981' : scoreVal >= 40 ? '#f59e0b' : '#ef4444' }}>{scoreVal.toFixed(1)}</Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: '#b0b8c4' }}>/100</Typography>
                    <Typography sx={{ fontSize: '0.5rem', color: '#cbd5e1', mx: 0.3 }}>│</Typography>
                    <Typography sx={{ fontSize: '0.64rem', color: '#94a3b8' }}>{T('detail.priceLabel')}</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, fontFamily: 'monospace', color: '#1e293b', lineHeight: 1 }}>{detailRow.close?.toLocaleString()}</Typography>
                  </Box>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setDetailTicker(null)} sx={{ color: '#475569' }}><Close sx={{ fontSize: 20 }} /></IconButton>
            </Box>

            <Box sx={{ p: 0, overflowY: 'auto' }}>
              {/* ── Verdict banner ── */}
              {(() => { const p = PRIORITY_CFG[detailRow.priority] || PRIORITY_CFG['B']; return (
                <Box sx={{ mx: 2, mt: 1.5, mb: 1, p: 1.4, borderRadius: 1.5, bgcolor: alpha(p.color, 0.07), border: `1px solid ${alpha(p.color, 0.2)}`, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.58rem', textAlign: 'center', lineHeight: 1.3, px: 0.3 }}>{t(p.key)}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: p.color, fontSize: '0.92rem', lineHeight: 1.2 }}>{t(p.key)}</Typography>
                    <Typography sx={{ fontSize: '0.63rem', color: '#64748b', mt: 0.15 }}>{T('detail.prioritySubtitle', { priority: detailRow.priority, setup: setupLabel(setup.key) })}</Typography>
                  </Box>
                </Box>
              ); })()}

              {/* ── Metric grid ── */}
              <Box sx={{ mx: 2, mb: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                {[
                  { label: T('detail.metricStrength5d'), value: fmtPct(detailRow.excess_ret_5d), good: (detailRow.excess_ret_5d || 0) > 0 },
                  { label: T('detail.metricGreenOnRed'), value: `${detailRow.green_when_index_red_10 ?? 0}/10 ${T('detail.sessions')}`, good: (detailRow.green_when_index_red_10 || 0) >= 3 },
                  { label: T('detail.metricRet20d'), value: fmtPct(detailRow.ret_20d), good: (detailRow.ret_20d || 0) > 0, warn: (detailRow.ret_20d || 0) > 0.25 },
                  { label: T('detail.metricNearHigh'), value: `${detailRow.close_near_high_5 ?? 0}/5 ${T('detail.sessions')}`, good: (detailRow.close_near_high_5 || 0) >= 2 },
                  { label: T('detail.metricLiquidity'), value: fmtBillion(detailRow.avg_value_20d), good: (detailRow.avg_value_20d || 0) >= 20e9 },
                  { label: T('detail.metricVolRatio'), value: `${detailRow.vol_ratio_20?.toFixed(2) ?? '—'}×`, good: (detailRow.vol_ratio_20 || 0) >= 1 },
                  { label: T('detail.metricVolatility'), value: detailRow.atr_pct_10 != null ? `${(detailRow.atr_pct_10 * 100).toFixed(1)}%` : '—', warn: (detailRow.atr_pct_10 || 0) > 0.05, good: (detailRow.atr_pct_10 || 0.03) <= 0.05 },
                  { label: T('detail.metricNearLow'), value: `${detailRow.close_near_low_5 ?? 0}/5 ${T('detail.sessions')}`, warn: (detailRow.close_near_low_5 || 0) >= 3, good: (detailRow.close_near_low_5 || 5) <= 1 },
                ].map((m, i) => (
                  <Box key={i} sx={{ bgcolor: '#fff', px: 1.4, py: 1 }}>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8', lineHeight: 1 }}>{m.label}</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, fontFamily: 'monospace', lineHeight: 1.4, mt: 0.3,
                      color: m.warn ? '#ef4444' : m.good ? '#10b981' : '#1e293b' }}>{m.value}</Typography>
                  </Box>
                ))}
              </Box>

              {/* ── RSI ── */}
              <Box sx={{ mx: 2, mb: 1.5, px: 1.4, py: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8' }}>RSI 14</Typography>
                  <Typography sx={{ fontSize: '0.7rem', mt: 0.3, color: rsiVal > 70 ? '#ef4444' : rsiVal < 30 ? '#10b981' : '#475569' }}>
                    {rsiVal > 70 ? T('detail.rsiOverboughtDesc') : rsiVal < 30 ? T('detail.rsiOversoldDesc') : T('detail.rsiNeutralDesc')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, fontFamily: 'monospace',
                    color: rsiVal > 70 ? '#ef4444' : rsiVal < 30 ? '#10b981' : '#1e293b' }}>{rsiVal.toFixed(0)}</Typography>
                  <Box sx={{ width: 52, height: 5, borderRadius: 3, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ width: `${rsiVal}%`, height: '100%', borderRadius: 3,
                      bgcolor: rsiVal > 70 ? '#ef4444' : rsiVal < 30 ? '#10b981' : '#6366f1' }} />
                  </Box>
                </Box>
              </Box>

              {/* ── Lý do ── */}
              <Box sx={{ mx: 2, mb: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: alpha('#10b981', 0.03), border: `1px solid ${alpha('#10b981', 0.1)}` }}>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#059669', mb: 0.4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{T('detail.reasonTitle')}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.6 }}>{detailRow.reason}</Typography>
                </Box>
              </Box>
            </Box>
          </>
        );
        })()}
      </Drawer>

      {/* ── Watchlist Detail Drawer ─────────────────────────────── */}
      <Drawer anchor="right" open={!!wlDetailRow} onClose={() => setWlDetailTicker(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 0 } }}>
        {wlDetailRow && (() => {
          const earlyCfg = getEarlyCfg(wlDetailRow.early_setup);
          const scoreVal = (wlDetailRow.positive_div_score || 0) * 100;
          const rsiVal = wlDetailRow.rsi14 || 50;
          const scoreColor = '#f59e0b';
          return (
          <>
            {/* Header */}
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                {/* Ticker logo box */}
                <Box sx={{ width: 48, height: 48, borderRadius: 0.75, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ flex: 1, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.82rem', lineHeight: 1, letterSpacing: '0.02em' }}>{wlDetailRow.ticker}</Typography>
                  </Box>
                  <Box sx={{ height: 13, bgcolor: earlyCfg.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '0.48rem', letterSpacing: '0.1em', lineHeight: 1, px: 0.3, textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 44 }}>{TW(`setup.${wlDetailRow.early_setup}`)}</Typography>
                  </Box>
                </Box>
                {/* Info area */}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>{wlDetailRow.ticker}</Typography>
                    <Chip label={TW('detail.signalChip', { count: wlDetailRow.early_signal_count ?? 0 })} size="small"
                      sx={{ bgcolor: alpha('#f59e0b', 0.08), color: '#d97706', fontWeight: 700, fontSize: '0.6rem', height: 18, px: 0.3 }} />
                    {(() => { const p = PRIORITY_CFG[wlDetailRow.priority] || PRIORITY_CFG['B']; return (
                      <Chip label={t(p.key)} size="small" sx={{ fontWeight: 800, fontSize: '0.6rem', height: 18, bgcolor: p.bg, color: p.color, border: `1px solid ${alpha(p.color, 0.3)}` }} />
                    ); })()}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.35, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.64rem', color: '#94a3b8' }}>{TW('detail.scoreLabel')}</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 900, fontFamily: 'monospace', color: scoreColor, lineHeight: 1 }}>{scoreVal.toFixed(1)}</Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: '#b0b8c4' }}>/100</Typography>
                    <Typography sx={{ fontSize: '0.5rem', color: '#cbd5e1', mx: 0.3 }}>│</Typography>
                    <Typography sx={{ fontSize: '0.64rem', color: '#94a3b8' }}>{TW('detail.priceLabel')}</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, fontFamily: 'monospace', color: '#1e293b', lineHeight: 1 }}>{wlDetailRow.close?.toLocaleString()}</Typography>
                  </Box>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setWlDetailTicker(null)} sx={{ color: '#475569' }}><Close sx={{ fontSize: 20 }} /></IconButton>
            </Box>

            <Box sx={{ p: 0, overflowY: 'auto' }}>
              {/* ── Verdict banner ── */}
              {(() => { const p = PRIORITY_CFG[wlDetailRow.priority] || PRIORITY_CFG['B']; return (
                <Box sx={{ mx: 2, mt: 1.5, mb: 1, p: 1.4, borderRadius: 1.5, bgcolor: alpha(p.color, 0.07), border: `1px solid ${alpha(p.color, 0.2)}`, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.58rem', textAlign: 'center', lineHeight: 1.3, px: 0.3 }}>{t(p.key)}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: p.color, fontSize: '0.92rem', lineHeight: 1.2 }}>{t(p.key)}</Typography>
                    <Typography sx={{ fontSize: '0.63rem', color: '#64748b', mt: 0.15 }}>{TW('detail.prioritySubtitleWl', { priority: wlDetailRow.priority, setup: TW(`setup.${wlDetailRow.early_setup}`), count: wlDetailRow.early_signal_count ?? 0 })}</Typography>
                  </Box>
                </Box>
              ); })()}

              {/* ── Metric grid ── */}
              <Box sx={{ mx: 2, mb: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                {[
                  { label: TW('detail.metricStrength5d'), value: fmtPct(wlDetailRow.excess_ret_5d), good: (wlDetailRow.excess_ret_5d || 0) > 0 },
                  { label: TW('detail.metricGreenOnRed'), value: `${wlDetailRow.green_when_index_red_10 ?? 0}/10 ${TW('detail.sessions')}`, good: (wlDetailRow.green_when_index_red_10 || 0) >= 3 },
                  { label: TW('detail.metricRet5d'), value: fmtPct(wlDetailRow.ret_5d), good: (wlDetailRow.ret_5d || 0) > 0 },
                  { label: TW('detail.metricRet20d'), value: fmtPct(wlDetailRow.ret_20d), good: (wlDetailRow.ret_20d || 0) > 0 },
                  { label: TW('detail.metricLiquidity'), value: fmtBillionW(wlDetailRow.avg_value_20d), good: (wlDetailRow.avg_value_20d || 0) >= 20e9 },
                  { label: TW('detail.metricVolRatio'), value: `${wlDetailRow.vol_ratio_20?.toFixed(2) ?? '—'}×`, good: (wlDetailRow.vol_ratio_20 || 0) >= 1 },
                  { label: TW('detail.metricVolatility'), value: wlDetailRow.atr_pct_10 != null ? `${(wlDetailRow.atr_pct_10 * 100).toFixed(1)}%` : '—', warn: (wlDetailRow.atr_pct_10 || 0) > 0.05, good: (wlDetailRow.atr_pct_10 || 0.03) <= 0.05 },
                  { label: TW('detail.metricNearLow'), value: `${wlDetailRow.close_near_low_5 ?? 0}/5 ${TW('detail.sessions')}`, warn: (wlDetailRow.close_near_low_5 || 0) >= 3, good: (wlDetailRow.close_near_low_5 || 5) <= 1 },
                ].map((m, i) => (
                  <Box key={i} sx={{ bgcolor: '#fff', px: 1.4, py: 1 }}>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8', lineHeight: 1 }}>{m.label}</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, fontFamily: 'monospace', lineHeight: 1.4, mt: 0.3,
                      color: m.warn ? '#ef4444' : m.good ? '#10b981' : '#1e293b' }}>{m.value}</Typography>
                  </Box>
                ))}
              </Box>

              {/* ── RSI ── */}
              <Box sx={{ mx: 2, mb: 1.5, px: 1.4, py: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8' }}>RSI 14</Typography>
                  <Typography sx={{ fontSize: '0.7rem', mt: 0.3, color: rsiVal > 70 ? '#ef4444' : rsiVal < 30 ? '#10b981' : '#475569' }}>
                    {rsiVal > 70 ? TW('detail.rsiOverboughtDesc') : rsiVal < 30 ? TW('detail.rsiOversoldDesc') : TW('detail.rsiNeutralDesc')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, fontFamily: 'monospace',
                    color: rsiVal > 70 ? '#ef4444' : rsiVal < 30 ? '#10b981' : '#1e293b' }}>{rsiVal.toFixed(0)}</Typography>
                  <Box sx={{ width: 52, height: 5, borderRadius: 3, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ width: `${rsiVal}%`, height: '100%', borderRadius: 3,
                      bgcolor: rsiVal > 70 ? '#ef4444' : rsiVal < 30 ? '#10b981' : '#6366f1' }} />
                  </Box>
                </Box>
              </Box>

              {/* ── Lý do ── */}
              <Box sx={{ mx: 2, mb: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: alpha('#f59e0b', 0.03), border: `1px solid ${alpha('#f59e0b', 0.1)}` }}>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#d97706', mb: 0.4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{TW('detail.reasonTitle')}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.6 }}>{wlDetailRow.early_reason}</Typography>
                </Box>
              </Box>
            </Box>
          </>
        );
        })()}
      </Drawer>

      {/* ── Error snackbar ──────────────────────────────────────── */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}

// ── Shared sub-components ────────────────────────────────────────────
function MarketPulseCard({ summary }) {
  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden',
      minWidth: 260, borderRadius: 1.5, px: 2.5, py: 1.8,
      bgcolor: '#fff', border: '1px solid #e2e8f0',
      display: 'flex', alignItems: 'center', gap: 2.5,
    }}>
      <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(summary.market_weak ? '#ef4444' : '#10b981', 0.06)} 0%, transparent 70%)` }} />
      <Box sx={{
        width: 40, height: 40, borderRadius: 2, flexShrink: 0,
        background: summary.market_weak
          ? `linear-gradient(135deg, #ef4444 0%, ${alpha('#ef4444', 0.7)} 100%)`
          : `linear-gradient(135deg, #10b981 0%, ${alpha('#10b981', 0.7)} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: summary.market_weak ? `0 3px 8px ${alpha('#ef4444', 0.25)}` : `0 3px 8px ${alpha('#10b981', 0.25)}`,
      }}>
        {summary.market_weak
          ? <TrendingDown sx={{ fontSize: 20, color: '#fff' }} />
          : <TrendingUp sx={{ fontSize: 20, color: '#fff' }} />
        }
      </Box>
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-end', position: 'relative' }}>
        <Box>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', lineHeight: 1, mb: 0.3 }}>VNINDEX 5D</Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, fontFamily: 'monospace', lineHeight: 1, color: (summary.index_ret_5d || 0) < 0 ? '#ef4444' : '#10b981' }}>
            {fmtPct(summary.index_ret_5d)}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', lineHeight: 1, mb: 0.3 }}>10D</Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, fontFamily: 'monospace', lineHeight: 1, color: (summary.index_ret_10d || 0) < 0 ? '#ef4444' : '#10b981' }}>
            {fmtPct(summary.index_ret_10d)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function StatTile({ label, val, color, Icon }) {
  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden',
      flex: '1 1 120px', minWidth: 110, borderRadius: 1.5,
      border: `1px solid ${alpha(color, 0.12)}`, bgcolor: '#fff',
      px: 2, py: 1.5, transition: 'all 0.2s',
      '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
    }}>
      <Box sx={{ position: 'absolute', top: 0, right: 0, width: 56, height: 56, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
          background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 2px 6px ${alpha(color, 0.25)}`,
        }}>
          <Icon sx={{ fontSize: 16, color: '#fff' }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', lineHeight: 1, mb: 0.3 }}>{label}</Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, color, lineHeight: 1, fontFamily: 'monospace' }}>{val}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

function SearchField({ value, onChange, placeholder, accent }) {
  return (
    <TextField
      size="small" placeholder={placeholder} value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
      sx={{
        width: 180,
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem', borderRadius: 2, height: 32,
          '& fieldset': { borderColor: '#e2e8f0' },
          '&.Mui-focused fieldset': { borderColor: accent },
        },
      }}
    />
  );
}

// ── Market Context Strip ─────────────────────────────────────────────
const REGIME_CFG = {
  RISK_ON:  { label: 'Risk On',  color: '#10b981', bg: '#ecfdf5' },
  RISK_OFF: { label: 'Risk Off', color: '#ef4444', bg: '#fef2f2' },
  NEUTRAL:  { label: 'Neutral',  color: '#f59e0b', bg: '#fffbeb' },
  ALLOW:    { label: 'Allow',    color: '#10b981', bg: '#ecfdf5' },
  WATCH:    { label: 'Watch',    color: '#f59e0b', bg: '#fffbeb' },
  AVOID:    { label: 'Avoid',    color: '#ef4444', bg: '#fef2f2' },
};

function MarketContextStrip({ summary }) {
  const ctx = summary?.market_context;
  if (!ctx || Object.keys(ctx).length === 0) return null;

  const regime = ctx.regime;
  const rotation = ctx.rotation;
  const niche = ctx.niche;
  const topSectors = ctx.top_sectors;

  const regimeLabel = regime?.label || regime?.alert_level;
  const rcfg = REGIME_CFG[regimeLabel] || REGIME_CFG.NEUTRAL;
  const breadthPct = regime?.breadth_pct_above20;
  const isNiche = niche?.is_niche;

  return (
    <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {/* Regime chip */}
      {regimeLabel && (
        <Chip
          icon={<SecurityOutlined sx={{ fontSize: 13 }} />}
          label={`${rcfg.label}${regime?.risk_score != null ? ` (${regime.risk_score})` : ''}`}
          size="small"
          sx={{
            fontSize: '0.68rem', fontWeight: 700, height: 24,
            bgcolor: alpha(rcfg.color, 0.08), color: rcfg.color,
            border: `1px solid ${alpha(rcfg.color, 0.15)}`,
            '& .MuiChip-icon': { color: rcfg.color },
          }}
        />
      )}

      {/* Breadth chip */}
      {breadthPct != null && (
        <Chip
          icon={<BarChart sx={{ fontSize: 13 }} />}
          label={`Breadth ${breadthPct.toFixed(0)}%`}
          size="small"
          sx={{
            fontSize: '0.68rem', fontWeight: 700, height: 24,
            bgcolor: alpha(breadthPct >= 50 ? '#10b981' : breadthPct >= 30 ? '#f59e0b' : '#ef4444', 0.08),
            color: breadthPct >= 50 ? '#059669' : breadthPct >= 30 ? '#d97706' : '#dc2626',
            border: `1px solid ${alpha(breadthPct >= 50 ? '#10b981' : breadthPct >= 30 ? '#f59e0b' : '#ef4444', 0.15)}`,
            '& .MuiChip-icon': { color: breadthPct >= 50 ? '#059669' : breadthPct >= 30 ? '#d97706' : '#dc2626' },
          }}
        />
      )}

      {/* Rotation chip */}
      {rotation?.is_rotation && (
        <Chip
          icon={<SyncAlt sx={{ fontSize: 13 }} />}
          label={`Xoay ngành (${rotation.events} sự kiện)`}
          size="small"
          sx={{
            fontSize: '0.68rem', fontWeight: 700, height: 24,
            bgcolor: alpha('#8b5cf6', 0.08), color: '#7c3aed',
            border: `1px solid ${alpha('#8b5cf6', 0.15)}`,
            '& .MuiChip-icon': { color: '#7c3aed' },
          }}
        />
      )}

      {/* Niche chip */}
      {isNiche && (
        <Chip
          icon={<TrackChanges sx={{ fontSize: 13 }} />}
          label={`Niche (${niche.score}/5)`}
          size="small"
          sx={{
            fontSize: '0.68rem', fontWeight: 700, height: 24,
            bgcolor: alpha('#6366f1', 0.08), color: '#4f46e5',
            border: `1px solid ${alpha('#6366f1', 0.15)}`,
            '& .MuiChip-icon': { color: '#4f46e5' },
          }}
        />
      )}

      {/* Separator + top sectors */}
      {topSectors?.length > 0 && (
        <>
          <Box sx={{ width: '1px', height: 16, bgcolor: '#e2e8f0', mx: 0.3 }} />
          <CategoryOutlined sx={{ fontSize: 13, color: '#94a3b8' }} />
          {topSectors.slice(0, 3).map((s) => (
            <Tooltip key={s.code} title={`${s.name} — Cơ hội: ${s.opportunity} / Rủi ro: ${s.risk}`} arrow>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.3,
                px: 0.7, py: 0.15, borderRadius: 1,
                bgcolor: alpha(s.quadrant === 'LEADING' ? '#10b981' : s.quadrant === 'IMPROVING' ? '#3b82f6' : '#94a3b8', 0.06),
                border: `1px solid ${alpha(s.quadrant === 'LEADING' ? '#10b981' : s.quadrant === 'IMPROVING' ? '#3b82f6' : '#94a3b8', 0.12)}`,
              }}>
                <FiberManualRecord sx={{ fontSize: 6, color: s.quadrant === 'LEADING' ? '#10b981' : s.quadrant === 'IMPROVING' ? '#3b82f6' : '#94a3b8' }} />
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#475569' }}>{s.name || s.code}</Typography>
              </Box>
            </Tooltip>
          ))}
        </>
      )}
    </Box>
  );
}
